import React, { useState } from "react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    ? "http://localhost:5000"
    : "https://api.quote.softvolute.in");

// ============================================================
// ROLE NORMALIZER
// ============================================================

const normalizeRole = (value = "") => {
  return String(value).trim().toLowerCase().replace(/\s+/g, "_");
};

// ============================================================
// LOGIN
// ============================================================

const Login = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // FETCH ROLE PERMISSIONS
  // ============================================================

  const fetchRolePermissions = async (token, user) => {
    try {
      const userRole = normalizeRole(
        user?.role || user?.roleKey || user?.roleName || "",
      );

      console.log("Logged in role:", userRole);

      // Admin has automatic full access through canAccess()
      // so role permissions are not mandatory for admin.
      if (userRole === "admin") {
        localStorage.setItem("permissions", JSON.stringify({}));

        return {};
      }

      const res = await fetch(`${API_BASE_URL}/api/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));

        throw new Error(
          errorData?.message || `Unable to load permissions (${res.status})`,
        );
      }

      const roleData = await res.json();

      console.log("Roles received:", roleData);

      const roles = Array.isArray(roleData)
        ? roleData
        : Array.isArray(roleData?.roles)
          ? roleData.roles
          : [];

      const matchedRole = roles.find((role) => {
        const roleKey = normalizeRole(
          role?.roleKey || role?.role || role?.roleName || role?.name || "",
        );

        return roleKey === userRole;
      });

      if (!matchedRole) {
        console.warn(`No saved role permissions found for: ${userRole}`);

        localStorage.setItem("permissions", JSON.stringify({}));

        return {};
      }

      const permissions =
        matchedRole?.permissions && typeof matchedRole.permissions === "object"
          ? matchedRole.permissions
          : {};

      localStorage.setItem("permissions", JSON.stringify(permissions));

      console.log("Permissions saved:", permissions);

      return permissions;
    } catch (err) {
      console.error("Permission fetch error:", err);

      // Do not keep previous user's permissions
      localStorage.setItem("permissions", JSON.stringify({}));

      throw err;
    }
  };

  // ============================================================
  // LOGIN SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      // Clear stale data from previous user
      localStorage.removeItem("permissions");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");

      // --------------------------------------------------------
      // LOGIN API
      // --------------------------------------------------------

      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      let data = {};

      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid response from server");
      }

      if (!res.ok) {
        throw new Error(
          data?.message || data?.error || `Login failed (${res.status})`,
        );
      }

      if (!data?.token) {
        throw new Error("Token not received from server");
      }

      if (!data?.user) {
        throw new Error("User information not received from server");
      }

      // --------------------------------------------------------
      // SAVE TOKEN
      // --------------------------------------------------------

      localStorage.setItem("token", data.token);

      // --------------------------------------------------------
      // SAVE USER
      // --------------------------------------------------------

      localStorage.setItem("user", JSON.stringify(data.user));

      // --------------------------------------------------------
      // SAVE ROLE
      // --------------------------------------------------------

      const normalizedRole = normalizeRole(
        data.user?.role || data.user?.roleKey || data.user?.roleName || "",
      );

      if (normalizedRole) {
        // Saving both so existing code remains compatible
        localStorage.setItem("role", normalizedRole);

        localStorage.setItem("userRole", normalizedRole);
      }

      // --------------------------------------------------------
      // LOAD PERMISSIONS
      // --------------------------------------------------------

      try {
        await fetchRolePermissions(data.token, data.user);
      } catch (permissionError) {
        console.error("Could not load permissions:", permissionError);

        /*
          We intentionally do not stop login here.

          User can still log in, but permission-controlled
          modules will remain hidden until permissions can
          be loaded.
        */
      }

      // --------------------------------------------------------
      // LOGIN SUCCESS
      // --------------------------------------------------------

      onLoginSuccess(data.token, data.user);
    } catch (err) {
      console.error("Login error:", err);

      // Login failed, remove partial auth data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("role");
      localStorage.removeItem("userRole");
      localStorage.removeItem("permissions");

      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Softvolute Buildcon LLP</h1>

      <div style={styles.card}>
        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* EMAIL */}

          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={styles.input}
          />

          {/* PASSWORD */}

          <div style={styles.passwordWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={{
                ...styles.input,
                paddingRight: "65px",
              }}
            />

            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              style={styles.toggleButton}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>

          {/* LOGIN BUTTON */}

          <button
            type="submit"
            style={{
              ...styles.button,

              opacity: loading ? 0.7 : 1,

              cursor: loading ? "not-allowed" : "pointer",
            }}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#e6e6e6",
    padding: "20px",
    boxSizing: "border-box",
  },

  title: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#2c3e50",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    marginBottom: "20px",
  },

  card: {
    backgroundColor: "#fff",
    padding: "30px 20px",
    borderRadius: "8px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "420px",
    textAlign: "center",
    boxSizing: "border-box",
  },

  error: {
    color: "red",
    fontSize: "14px",
    marginBottom: "10px",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  input: {
    padding: "12px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "16px",
    width: "100%",
    backgroundColor: "#eef3ff",
    boxSizing: "border-box",
  },

  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },

  toggleButton: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "14px",
    color: "#007BFF",
  },

  button: {
    padding: "14px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "18px",
    width: "100%",
    marginTop: "10px",
  },
};

export default Login;
